import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";

import type { AssistantResponse } from "../../shared/contracts";
import iconUrl from "../assets/Watchpoint_Icon.png";
import type { ConversationSummary } from "../conversation-store";
import {
  CheckIcon,
  ChevronIcon,
  CloseIcon,
  ContractIcon,
  CopyIcon,
  CrosshairIcon,
  ExpandIcon,
  HistoryIcon,
  NewChatIcon,
  SparkIcon,
} from "../icons";
import { renderMarkdown } from "../markdown";

/** One exchange. `response` and `failure` are mutually exclusive and arrive late. */
export interface ChatTurn {
  question: string;
  context?: string; // set by Task 8
  response?: AssistantResponse;
  failure?: string;
}

const starterPrompts = [
  "Why did EC2 spend increase this week?",
  "What are my top five services this month?",
  "How much untagged spend do I have?",
  "Compare this month's spend to last month.",
];

const stopReasonNotes: Record<AssistantResponse["stopped"], string> = {
  complete: "",
  iteration_cap: "Stopped after the maximum number of query steps.",
  repair_budget: "Stopped after the query could not be made safe.",
  deadline: "Stopped at the time limit for one answer; this may be partial.",
  max_tokens: "Stopped at the length limit for one answer; this is cut short.",
};

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Provenance for the figures above it. The assistant is not allowed to state a
 * number without a query behind it, so showing the queries is what makes that
 * claim checkable rather than something the user has to trust.
 */
function CopyButton({ text }: { text: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const copy = useCallback(() => {
    if (!("clipboard" in navigator) || !navigator.clipboard) {
      setState("failed");
      return;
    }
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setState("copied");
      })
      .catch(() => {
        setState("failed");
      });
  }, [text]);
  useEffect(() => {
    if (state === "idle") return;
    const timer = window.setTimeout(() => {
      setState("idle");
    }, 2000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [state]);
  return (
    <button
      className="assistant__copy"
      type="button"
      onClick={copy}
      aria-label={state === "copied" ? "Copied" : "Copy answer"}
    >
      {state === "copied" ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
      <span>
        {state === "copied"
          ? "Copied"
          : state === "failed"
            ? "Copy failed"
            : "Copy"}
      </span>
    </button>
  );
}

function AssistantEvidencePanel({ response }: { response: AssistantResponse }) {
  const note = stopReasonNotes[response.stopped];
  const { evidence } = response;
  const scanned = evidence.queries.reduce(
    (sum, query) => sum + query.scannedBytes,
    0,
  );

  return (
    <div className="assistant__evidence">
      {note && <p className="assistant__note">{note}</p>}
      {response.memoryDegraded && (
        <p className="assistant__note">
          Past conversations were unavailable for this answer.
        </p>
      )}
      {response.memoriesUsed.length > 0 && (
        <p className="assistant__note">
          Recalled: {response.memoriesUsed.map((m) => m.title).join("; ")}
        </p>
      )}

      {evidence.queries.length === 0 ? (
        <p className="assistant__note">
          No CUR query ran, so this answer carries no account-specific figures.
        </p>
      ) : (
        <details>
          <summary>
            <ChevronIcon size={14} />
            Evidence — {evidence.queries.length}{" "}
            {evidence.queries.length === 1 ? "query" : "queries"} ·{" "}
            {formatBytes(scanned)} scanned
          </summary>
          <dl className="assistant__lineage">
            <dt>Export</dt>
            <dd>{evidence.exportName}</dd>
            <dt>Table</dt>
            <dd>
              {evidence.glueDatabase}.{evidence.glueTable}
            </dd>
            <dt>Partitions</dt>
            <dd>{evidence.partitions.join(", ") || "—"}</dd>
            <dt>Prefix</dt>
            <dd className="assistant__prefix">{evidence.s3Prefix}</dd>
            <dt>Columns</dt>
            <dd>{evidence.columns.join(", ") || "—"}</dd>
          </dl>
          {evidence.queries.map((query) => (
            <div key={query.queryExecutionId} className="assistant__query">
              <p>
                <code>{query.queryExecutionId}</code> —{" "}
                {formatBytes(query.scannedBytes)} scanned, {query.rowCount}{" "}
                {query.rowCount === 1 ? "row" : "rows"}
              </p>
              <pre>
                <code>{query.sql}</code>
              </pre>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

/**
 * The docked chat column became a floating FAB + overlay panel: the panel is
 * conditionally rendered, but the FAB stays mounted so it can always toggle
 * the panel back open and carry the unread indicator.
 */
export function Assistant({
  open,
  unread,
  turns,
  pending,
  context,
  history,
  activeConversationId,
  onToggle,
  onClearContext,
  onAsk,
  onNewConversation,
  onOpenConversation,
  onDeleteConversation,
  onEnterAskMode,
  askDisabled,
}: {
  open: boolean;
  unread: boolean;
  turns: ChatTurn[];
  pending: boolean;
  context: string | null;
  history: ConversationSummary[];
  activeConversationId: string;
  onToggle: () => void;
  onClearContext: () => void;
  onAsk: (message: string) => void;
  onNewConversation: () => void;
  onOpenConversation: (id: string) => void;
  onDeleteConversation: (id: string) => void;
  onEnterAskMode: () => void;
  askDisabled: boolean;
}) {
  // Controlled on purpose: the composer used to be uncontrolled and cleared
  // with form.reset(), which bypasses React's value tracking — the next
  // keystroke's re-render restored the sent text, so a follow-up question
  // arrived with the previous one silently prepended.
  const [draft, setDraft] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState<"chat" | "history">("chat");
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  const resizeComposer = useCallback(() => {
    const field = composerRef.current;
    if (!field) return;
    field.style.height = "auto";
    field.style.height = `${String(Math.min(field.scrollHeight, 160))}px`;
  }, []);

  const ask = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const message = draft.trim();
      // Enter is the only send affordance, so it must not eat a draft while
      // an answer is still pending — keep the text for the user to resend.
      if (!message || pending) return;
      setDraft("");
      resizeComposer();
      onAsk(message);
    },
    [draft, resizeComposer, onAsk, pending],
  );

  const composerChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setDraft(event.currentTarget.value);
      resizeComposer();
    },
    [resizeComposer],
  );

  const composerKey = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== "Enter" || event.shiftKey) return;
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    },
    [],
  );

  const trackChatScroll = useCallback(() => {
    const body = chatBodyRef.current;
    if (!body) return;
    stickToBottom.current =
      body.scrollHeight - body.scrollTop - body.clientHeight < 96;
  }, []);

  // Follow the newest message unless the reader has scrolled up to study one.
  useEffect(() => {
    const body = chatBodyRef.current;
    if (body && stickToBottom.current) body.scrollTop = body.scrollHeight;
  }, [turns, pending]);

  // A picked context means the panel just reopened to receive a question —
  // put the cursor where the user will type it.
  useEffect(() => {
    if (context) composerRef.current?.focus();
  }, [context]);

  return (
    <div className="assistant-dock">
      {open && (
        <section
          className={
            expanded
              ? "assistant-panel assistant-panel--expanded"
              : "assistant-panel"
          }
          aria-label="Cost assistant"
        >
          <header className="assistant__header">
            <div>
              <img className="assistant__icon" src={iconUrl} alt="" />
              <strong>Watchpoint Assistant</strong>
            </div>
            <div className="assistant__actions">
              <button
                className="icon-button"
                type="button"
                onClick={() => {
                  setView((current) =>
                    current === "history" ? "chat" : "history",
                  );
                }}
                aria-pressed={view === "history"}
                aria-label="Chat history"
                title="Chat history"
              >
                <HistoryIcon />
              </button>
              <button
                className="icon-button"
                type="button"
                onClick={onEnterAskMode}
                disabled={askDisabled}
                aria-label="Ask about something on screen"
                title="Ask about something on screen"
              >
                <CrosshairIcon />
              </button>
              <button
                className="icon-button"
                type="button"
                onClick={() => {
                  onNewConversation();
                  setView("chat");
                }}
                disabled={turns.length === 0 || pending}
                aria-label="New chat"
                title="New chat"
              >
                <NewChatIcon />
              </button>
              <button
                className="icon-button"
                type="button"
                onClick={() => {
                  setExpanded((current) => !current);
                }}
                aria-label={expanded ? "Shrink assistant" : "Expand assistant"}
                title={expanded ? "Shrink assistant" : "Expand assistant"}
              >
                {expanded ? <ContractIcon /> : <ExpandIcon />}
              </button>
              <button
                className="icon-button"
                type="button"
                onClick={onToggle}
                aria-label="Close assistant"
                title="Close assistant"
              >
                <CloseIcon />
              </button>
            </div>
          </header>

          {view === "history" && (
            <div className="assistant__body">
              {history.length === 0 ? (
                <div className="assistant__empty">
                  <HistoryIcon size={28} />
                  <strong>No past conversations</strong>
                  <p>Threads you start are kept on this browser.</p>
                </div>
              ) : (
                <ul className="assistant__history">
                  {history.map((conversation) => (
                    <li
                      key={conversation.id}
                      className={
                        conversation.id === activeConversationId
                          ? "assistant__history-item assistant__history-item--active"
                          : "assistant__history-item"
                      }
                    >
                      <button
                        className="assistant__history-open"
                        type="button"
                        disabled={pending}
                        onClick={() => {
                          onOpenConversation(conversation.id);
                          setView("chat");
                        }}
                      >
                        <strong>{conversation.title}</strong>
                        <span>
                          {formatWhen(conversation.updatedAt)} ·{" "}
                          {conversation.turnCount}{" "}
                          {conversation.turnCount === 1
                            ? "question"
                            : "questions"}
                        </span>
                      </button>
                      <button
                        className="icon-button icon-button--row"
                        type="button"
                        disabled={pending}
                        onClick={() => {
                          onDeleteConversation(conversation.id);
                        }}
                        aria-label={`Delete conversation "${conversation.title}"`}
                        title="Delete conversation"
                      >
                        <CloseIcon size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {view === "chat" && (
            <>
              <div
                className="assistant__body"
                ref={chatBodyRef}
                onScroll={trackChatScroll}
              >
                {turns.length === 0 && (
                  <div className="assistant__empty">
                    <SparkIcon size={28} />
                    <strong>Ask about your AWS spend</strong>
                    <p>
                      Queries CUR directly and cites every figure. Follow-up
                      questions keep the thread.
                    </p>
                    <div className="assistant__starters">
                      {starterPrompts.map((prompt) => (
                        <button
                          key={prompt}
                          className="chip"
                          type="button"
                          disabled={pending}
                          onClick={() => onAsk(prompt)}
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {turns.map((turn, index) => (
                  <Fragment key={`${String(index)}-${turn.question}`}>
                    <div className="assistant__message assistant__message--question">
                      {turn.context && (
                        <p className="assistant__context-tag">
                          ✦ {turn.context}
                        </p>
                      )}
                      <p>{turn.question}</p>
                    </div>
                    {turn.failure && (
                      <div
                        className="assistant__message inline-message inline-message--error"
                        role="alert"
                      >
                        <p>{turn.failure}</p>
                      </div>
                    )}
                    {turn.response && (
                      <div
                        className="assistant__message assistant__message--answer"
                        aria-live="polite"
                      >
                        <img
                          className="assistant__avatar"
                          src={iconUrl}
                          alt=""
                        />
                        <div className="assistant__answer">
                          <div className="assistant__prose">
                            {renderMarkdown(turn.response.answer)}
                          </div>
                          <div className="assistant__answer-tools">
                            <CopyButton text={turn.response.answer} />
                          </div>
                          <AssistantEvidencePanel response={turn.response} />
                          {!pending &&
                            (turn.response.suggestedFollowUps ?? []).length >
                              0 && (
                              <div className="assistant__followups">
                                {(turn.response.suggestedFollowUps ?? []).map(
                                  (question) => (
                                    <button
                                      key={question}
                                      className="chip chip--spark"
                                      type="button"
                                      onClick={() => onAsk(question)}
                                    >
                                      <SparkIcon />
                                      {question}
                                    </button>
                                  ),
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </Fragment>
                ))}

                {pending && (
                  <div className="assistant__thinking" role="status">
                    <img className="assistant__avatar" src={iconUrl} alt="" />
                    <span className="assistant__dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                    Querying CUR…
                  </div>
                )}
              </div>

              <form className="assistant__composer" onSubmit={ask}>
                {context && (
                  <div className="assistant__context">
                    <SparkIcon />
                    <span>Asking about: {context}</span>
                    <button
                      className="icon-button icon-button--row"
                      type="button"
                      onClick={onClearContext}
                      aria-label="Remove context"
                    >
                      <CloseIcon size={12} />
                    </button>
                  </div>
                )}
                <label className="sr-only" htmlFor="assistant-message">
                  Ask about cost
                </label>
                <div className="assistant__field">
                  <textarea
                    id="assistant-message"
                    name="message"
                    ref={composerRef}
                    rows={1}
                    maxLength={2000}
                    placeholder="Ask about your AWS costs…"
                    value={draft}
                    onChange={composerChange}
                    onKeyDown={composerKey}
                    required
                  />
                </div>
                <div className="assistant__composer-meta">
                  <span>Read-only queries · Enter to send</span>
                  {draft.length >= 1800 && (
                    <span>{String(draft.length)}/2000</span>
                  )}
                </div>
              </form>
            </>
          )}
        </section>
      )}
      {!open && (
        <button
          className="assistant-fab__ask"
          type="button"
          onClick={onEnterAskMode}
          disabled={askDisabled}
          aria-label="Ask about something on screen"
          title="Ask about something on screen"
        >
          <CrosshairIcon size={16} />
        </button>
      )}
      <button
        className="assistant-fab"
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={
          unread
            ? "Open Watchpoint Assistant — new answer"
            : "Open Watchpoint Assistant"
        }
      >
        <SparkIcon size={20} />
        {unread && <span className="assistant-fab__dot" aria-hidden="true" />}
      </button>
    </div>
  );
}
